import { createContext, useContext } from "react";

/**
 * Host-provided widget actions for the dashboard AI widgets (`dailyBrief` /
 * `coachingNudge`). The host (PB-180) supplies the callbacks; the data-blind
 * body only *invokes* them — it never calls AIS / Player AI itself (ADR-0007 /
 * ADR-0012). The `askPlayerAI` argument is a **user-intent seed** (a question
 * the user would ask), NOT an LLM prompt — the same sanctioned pattern as
 * `SqlWidgetDef.askAiPrompt`.
 *
 * Mirrors the `kpi-catalog` / `entity-resolver` precedent: `useWidgetActions()`
 * returns `null` when no provider is mounted, so an affordance simply hides
 * (gate on `useWidgetActions()?.askPlayerAI`) instead of crashing. Co-located
 * with the widgets (NOT in `shell/`) so the generic `EntityDashboard` never
 * imports anything action-aware.
 */
export interface WidgetActions {
  /** Hand a user-intent prompt to the host's Player AI rail. */
  readonly askPlayerAI?: (prompt: string) => void;
  /**
   * Navigate to a project (projectPulse's clickable rows). The host wires this
   * once the Projects module ships; until then it is simply omitted, so the
   * rows render display-only (gate on `useWidgetActions()?.openProject`).
   */
  readonly openProject?: (projectId: string) => void;
  /**
   * Open the Improvement Work / Projects overview (projectPulse's "View all
   * projects" footer). Wired once the Projects module ships; until then it is
   * omitted, so the footer hides (gate on `useWidgetActions()?.openImprovementWork`).
   */
  readonly openImprovementWork?: () => void;
  /** Open one SOLVE in a shell-owned entity tab. */
  readonly openSolve?: (solveId: string, title: string) => void;
  /** Open the SOLVE module's library/home route. */
  readonly openSolveHome?: () => void;
  /**
   * Open one SOLVE's canvas AT a specific widget — the SOLVE Actions row
   * affordance.
   *
   * Separate from {@link openSolve} rather than an extra argument on it,
   * because it is a different promise: `openSolve` lands on the workspace,
   * this one additionally ASKS the canvas to reveal a widget, which the host
   * may or may not be able to honour (the phase may be hidden, the widget may
   * have been deleted). The body gates on its presence exactly like
   * `openProject`, so a host that cannot deep-link renders the rows
   * display-only rather than offering a click that goes nowhere useful.
   *
   * Navigation only. Nothing here edits, completes or re-statuses an action —
   * SOLVE Actions is a read model and the canvas stays the only editor.
   */
  readonly openSolveAction?: (target: {
    readonly solveId: string;
    readonly solveTitle: string;
    readonly widgetId: string;
  }) => void;
  /** Open one VSM in its workspace. */
  readonly openVsm?: (vsmId: string, title: string) => void;
  /** Open the VSM module library. */
  readonly openVsmHome?: () => void;
  /** Open an enabled module by opaque id; the host revalidates before routing. */
  readonly openModule?: (moduleId: string) => void;
  // Extend this interface HERE — do NOT stand up a parallel context. Future
  // widget host-actions (any "the body asks the host to do X") live on this
  // one seam.
}

const Ctx = createContext<WidgetActions | null>(null);

export const WidgetActionsProvider = Ctx.Provider;

export function useWidgetActions(): WidgetActions | null {
  return useContext(Ctx);
}
